/**
 * Last-activity timestamp persistence. Phase 5 (inactivity-after-tab-
 * close enforcement). Records a Unix epoch ms timestamp in
 * `localStorage` on every throttled activity tick so the boot-time
 * check inside `initializeMedplumClient` can detect a stale session
 * (no activity for >LOGOUT_AT_MS) and force a sign-out before any
 * authenticated UI renders.
 *
 * Stores **only a number** — no PHI, no profile metadata.
 *
 * @agentic connections
 */

const LAST_ACTIVITY_KEY = "connections.lastActivityAt";

/** Persist `now` to localStorage. Silent no-op when storage is
 *  unavailable (Safari private mode, quota exceeded, etc.).
 *  @agentic connections */
export const writeLastActivity = (now: number = Date.now()): void => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  } catch {
    // Ignore storage failures — fallback is per-tab inactivity timing.
  }
};

/** Read the persisted timestamp. Returns undefined when absent or
 *  unparseable. @agentic connections */
export const readLastActivity = (): number | undefined => {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!raw) return undefined;
    const value = Number.parseInt(raw, 10);
    return Number.isFinite(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

/** Remove the persisted timestamp. Used on explicit sign-out so the
 *  next sign-in starts with a clean slate. @agentic connections */
export const clearLastActivity = (): void => {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    // Ignore.
  }
};
